export const PALETTE = {
  red: '#C62828', redD: '#B71C1C', redL: '#F8E7E4', redB: '#E6BDB8',
  cream: '#FFFAF5', beige: '#EEE3D6', beigeD: '#EFE4D8', beigeB: '#DCCDBD',
  white: '#FFFFFF', border: '#E8DED2',
  text: '#1A0E06', sub: '#4A3728', muted: '#6B4F3A',
  s1: '0 2px 10px rgba(26,14,6,0.06)',
  s2: '0 4px 20px rgba(26,14,6,0.09)',
  s3: '0 8px 32px rgba(26,14,6,0.13)',
  font: "'DM Sans', 'Segoe UI', sans-serif",
  fontSerif: "'DM Serif Display', Georgia, serif",
};

function readEnvString(keys, fallback = '') {
  for (const key of keys) {
    const val = process.env[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return fallback;
}

function readEnvNumber(keys, fallback) {
  const raw = readEnvString(keys, '');
  if (!raw) return fallback;
  const num = Number(raw);
  return Number.isFinite(num) ? num : fallback;
}

function toSensorEnvToken(deviceId) {
  return (deviceId || '').toUpperCase().replace(/[^A-Z0-9]/g, '_');
}

const DEFAULT_SENSOR_IDS = ['device_001', 'device_002', 'device_003'];

export const SENSOR_IDS = (() => {
  const raw = readEnvString(['REACT_APP_SENSOR_IDS'], '');
  if (!raw) return DEFAULT_SENSOR_IDS;
  const parsed = raw.split(',').map((x) => x.trim()).filter(Boolean);
  return parsed.length ? parsed : DEFAULT_SENSOR_IDS;
})();

export function sensorSeriesKey(sensorId, sensorIds = SENSOR_IDS) {
  const idx = sensorIds.indexOf(sensorId);
  return idx >= 0 ? `d${idx + 1}` : null;
}

const DEFAULT_SENSOR_META = {
  device_001: {
    name: 'Unang Sensor',
    loc: 'Purok 3, Marawoy',
    coords: [13.9345, 121.1572],
    x: 120,
    y: 200,
    color: { line: '#1565C0', fill: 'rgba(21,101,192,0.15)' },
  },
  device_002: {
    name: 'Ikalawang Sensor',
    loc: 'Main Drainage, Marawoy',
    coords: [13.9385, 121.1648],
    x: 280,
    y: 130,
    color: { line: '#E65100', fill: 'rgba(230,81,0,0.15)' },
  },
  device_003: {
    name: 'Ikatlong Sensor',
    loc: 'Bridge, Marawoy',
    coords: [13.936, 121.161],
    x: 200,
    y: 160,
    color: { line: '#2E7D32', fill: 'rgba(46,125,50,0.15)' },
  },
};

export const ALERT_THRESHOLDS = (() => {
  // Sensor distance thresholds (cm from sensor to water surface — lower = more flooding)
  // MMDA-aligned: water height = MAX_CM - sensorDistance
  // Yellow  >= 20cm water → sensor <= 190
  // Orange  >= 48cm water → sensor <= 162
  // Red     >= 94cm water → sensor <= 116
  const ALERT    = readEnvNumber(['REACT_APP_ALERT_THRESHOLD_ALERT'],   190);
  const CRITICAL = readEnvNumber(['REACT_APP_ALERT_THRESHOLD_CRITICAL'], 162);
  const DANGER   = readEnvNumber(['REACT_APP_ALERT_THRESHOLD_DANGER'],   116);
  const MAX_CM   = readEnvNumber(['REACT_APP_ALERT_THRESHOLD_MAX'],      210);
  return { ALERT, CRITICAL, DANGER, MAX_CM };
})();

export const SENSOR_CONFIG = SENSOR_IDS.reduce((acc, id, idx) => {
  const token = toSensorEnvToken(id);
  const defaults = DEFAULT_SENSOR_META[id] || {
    name: `Sensor ${idx + 1}`,
    loc: id,
    coords: [13.936, 121.161],
    x: 200,
    y: 160,
    color: { line: '#546E7A', fill: 'rgba(84,110,122,0.15)' },
  };

  const lat = readEnvNumber([`REACT_APP_SENSOR_${idx + 1}_LAT`, `REACT_APP_SENSOR_${token}_LAT`], defaults.coords[0]);
  const lng = readEnvNumber([`REACT_APP_SENSOR_${idx + 1}_LNG`, `REACT_APP_SENSOR_${token}_LNG`], defaults.coords[1]);

  acc[id] = {
    name: readEnvString([`REACT_APP_SENSOR_${idx + 1}_NAME`, `REACT_APP_SENSOR_${token}_NAME`], defaults.name),
    loc: readEnvString([`REACT_APP_SENSOR_${idx + 1}_LOC`, `REACT_APP_SENSOR_${token}_LOC`], defaults.loc),
    coords: [lat, lng],
    x: readEnvNumber([`REACT_APP_SENSOR_${idx + 1}_X`, `REACT_APP_SENSOR_${token}_X`], defaults.x),
    y: readEnvNumber([`REACT_APP_SENSOR_${idx + 1}_Y`, `REACT_APP_SENSOR_${token}_Y`], defaults.y),
  };

  return acc;
}, {});

export const SENSOR_COLORS = SENSOR_IDS.reduce((acc, id, idx) => {
  const token = toSensorEnvToken(id);
  const defaults = DEFAULT_SENSOR_META[id]?.color || { line: '#546E7A', fill: 'rgba(84,110,122,0.15)' };
  const sensorName = SENSOR_CONFIG[id]?.name || `Sensor ${idx + 1}`;

  acc[id] = {
    line: readEnvString([`REACT_APP_SENSOR_${idx + 1}_LINE_COLOR`, `REACT_APP_SENSOR_${token}_LINE_COLOR`], defaults.line),
    fill: readEnvString([`REACT_APP_SENSOR_${idx + 1}_FILL_COLOR`, `REACT_APP_SENSOR_${token}_FILL_COLOR`], defaults.fill),
    label: readEnvString([`REACT_APP_SENSOR_${idx + 1}_LABEL`, `REACT_APP_SENSOR_${token}_LABEL`], sensorName),
  };

  return acc;
}, {});

export const REGION_BARANGAY = readEnvString(['REACT_APP_REGION_BARANGAY'], 'Brgy. Marawoy');
export const REGION_CITY = readEnvString(['REACT_APP_REGION_CITY'], 'Lipa City');
export const REGION_PROVINCE = readEnvString(['REACT_APP_REGION_PROVINCE'], 'Batangas');
export const REGION_CITY_PROVINCE = REGION_PROVINCE ? `${REGION_CITY}, ${REGION_PROVINCE}` : REGION_CITY;
export const REGION_FULL = [REGION_BARANGAY, REGION_CITY, REGION_PROVINCE].filter(Boolean).join(', ');